import { useState, useEffect } from 'react';
import { validateToken } from '@/libs/api';
import Cookies from 'js-cookie';
import { normalizeAccessToken } from '@/utils/authToken';

const isUnauthorizedError = (error: unknown) =>
  (error as { statusCode?: number })?.statusCode === 401;

export const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuthStatus = async () => {
    const rawToken = Cookies.get('accessToken');
    const token = normalizeAccessToken(rawToken);

    // If token is stored as "Bearer <jwt>", rewrite cookie to the raw JWT.
    if (token && token !== rawToken) {
      Cookies.set('accessToken', token);
    }

    if (!token) {
      setIsAuthenticated(false);
      setIsLoading(false);
      return;
    }

    try {
      // 验证token有效性
      const validatedAccessToken = normalizeAccessToken(await validateToken());
      if (validatedAccessToken) {
        Cookies.set('accessToken', validatedAccessToken);
        setIsAuthenticated(true);
      }
    } catch (error: unknown) {
      // token无效，清除本地存储
      console.error('Token validation failed:', error);
      if (isUnauthorizedError(error)) {
        Cookies.remove('accessToken');
        Cookies.remove('userId');
      }
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuthStatus();

    // 监听storage变化
    const handleStorageChange = async (e: StorageEvent) => {
      if (e.key === 'accessToken') {
        if (e.newValue) {
          try {
            await validateToken();
            setIsAuthenticated(true);
          } catch (error: unknown) {
            setIsAuthenticated(false);
            console.error('Token validation failed on storage change:', error);
            if (isUnauthorizedError(error)) {
              Cookies.remove('accessToken');
              Cookies.remove('userId');
            }
          }
        } else {
          setIsAuthenticated(false);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return {
    isAuthenticated,
    isLoading,
    checkAuthStatus, // 导出方法以便需要时手动检查
  };
};
